import React, { useState } from 'react';
import { X, Calendar, MapPin, Clock, Link as LinkIcon, Copy, Check } from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAuth } from '../../hooks/useAuth';
import { useActiveGroup } from '../../hooks/useActiveGroup';
import { useToast } from '../../contexts/ToastContext';

interface CreateGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGameCreated?: (game: any) => void;
}

const CreateGameModal: React.FC<CreateGameModalProps> = ({
  isOpen,
  onClose,
  onGameCreated
}) => {
  const { supabase } = useSupabase();
  const { user } = useAuth();
  const { activeGroup } = useActiveGroup();
  const { showSuccess, showError } = useToast();
  
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setTitle('');
    setDate('');
    setTime('');
    setLocation('');
    setError(null);
    setSuccess(false);
    setGeneratedLink(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const generateConfirmationLink = async () => {
    if (!user || !activeGroup) {
      setError('Usuário ou grupo não encontrado');
      return;
    }

    if (!title || !date || !time) {
      setError('Título, data e horário são obrigatórios');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newPublicToken = crypto.randomUUID();
      
      // Combinar data e hora no formato correto sem conversão de fuso horário
      // Usar o formato que preserva a data e hora local exata
      const combinedDateTime = `${date}T${time}:00`;
      
      // Criar o jogo no Supabase
     const { data: game, error: gameError } = await supabase
  .from('jogos')
  .insert({
    titulo: title,
    data: combinedDateTime,
    local: location,
    grupo_id: activeGroup.id,
    user_id: user.id,
    criado_por: user.id, // ← ESSENCIAL para funcionar com RLS

    times: null,
    public_token: newPublicToken
  })
  .select()
  .single();


      if (gameError) throw gameError; // This line was missing in the original diff

      // Gerar o link de confirmação
      const baseUrl = window.location.origin;
      const confirmationLink = `${baseUrl}/confirmar/${game.public_token}`;
      
      setGeneratedLink(confirmationLink);
      setSuccess(true);

      // Copiar para o clipboard
      try {
        await navigator.clipboard.writeText(confirmationLink);
        setCopied(true);
        showSuccess(
          'Link copiado!', 
          'O link de confirmação foi copiado para o clipboard'
        );
        setTimeout(() => setCopied(false), 3000);
      } catch (clipboardError) {
        console.warn('Erro ao copiar para clipboard:', clipboardError);
        showError(
          'Erro ao copiar',
          'Não foi possível copiar o link automaticamente'
        );
      }

      // Chamar callback se fornecido
      if (onGameCreated) {
        onGameCreated(game);
      }

      showSuccess(
        'Jogo criado com sucesso!',
        'Link de confirmação gerado e copiado para o clipboard'
      );

    } catch (err: any) {
      console.error('Erro ao criar jogo:', err);
      setError(err.message || 'Erro ao criar jogo');
      showError(
        'Erro ao criar jogo',
        err.message || 'Ocorreu um erro inesperado'
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      showSuccess('Link copiado!', 'Link copiado para o clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
      showError('Erro ao copiar', 'Não foi possível copiar o link');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
          <h2 className="text-xl font-bold">Criar Novo Jogo</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && generatedLink && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">Jogo criado com sucesso!</span>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                    Link de confirmação:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm border border-green-300 dark:border-green-700 rounded-lg bg-white dark:bg-dark-paper"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-1"
                      title="Copiar link"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                {copied && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    ✓ Link copiado para o clipboard!
                  </p>
                )}
              </div>
            </div>
          )}

          {!success && (
            <form onSubmit={(e) => { e.preventDefault(); generateConfirmationLink(); }}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium mb-1">
                    Título do Jogo *
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="input w-full"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: Pelada de Sábado"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="date" className="block text-sm font-medium mb-1">
                    Data *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      id="date"
                      className="input w-full pl-10"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 10)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-medium mb-1">
                    Horário *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="time"
                      id="time"
                      className="input w-full pl-10"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium mb-1">
                    Local
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      id="location"
                      className="input w-full pl-10"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Ex: Campo do Clube"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-outline"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !title || !date || !time}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Criando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4" />
                      <span>Gerar Link de Confirmação</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

          {success && (
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={handleClose}
                className="btn-primary"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGameModal;